import { Box, CardContent, IconButton, Typography } from '@mui/material'
import {
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import { useCallback, useEffect, useState } from 'react'
import {
  ICustomTooltip,
  IDataStatusGraph,
  ISerieStatusGraph,
  IStatusCSV,
  IStatusGraph,
} from '../../../domain'
import {
  ViewDataEmpty,
  ViewDataError,
  ViewDataLoading,
  CardBox,
  CardBoxTitle,
  IconCsvDownloader,
  TooltipGraph,
  TooltipInfo,
} from '../../../components'
import {
  formatNumberToScale,
  formatToLocaleString,
  formatDate,
} from '../../../utils'

interface DynamicStatusRequestsGraphProps {
  dataGraph: IStatusGraph
  isLoading?: boolean
  isError?: boolean
  filterBy: string[]
}

const headerCSV = [
  { label: 'Status', key: 'status' },
  { label: 'Requisições', key: 'requests' },
  { label: 'Data', key: 'date' },
]

export const DynamicStatusRequestsGraph = ({
  dataGraph,
  isLoading,
  isError,
  filterBy,
}: DynamicStatusRequestsGraphProps) => {
  const [dataShow, setDataShow] = useState<IStatusGraph>({} as IStatusGraph)
  const [activeLegend, setActiveLegend] = useState<string>('')
  const [dataCSV, setDataCSV] = useState<IStatusCSV[]>([])

  const handleActiveLegend = ({ dataKey }: any) => {
    setActiveLegend(dataKey)
  }

  const handleDisableLegend = () => {
    setActiveLegend('')
  }

  const CustomTooltip = ({ payload }: ICustomTooltip) => {
    if (!payload || !payload[0]) {
      return null
    }

    return (
      <TooltipGraph>
        <Typography>{payload[0].payload.axisX}</Typography>
        {payload.map((element: any, index: number) => {
          return (
            <Box key={index}>
              <Typography>
                {element.name.substr(0, 9)}:{' '}
                {formatToLocaleString(element.value)}
              </Typography>
            </Box>
          )
        })}
      </TooltipGraph>
    )
  }

  const handleDataShow = (data: IStatusGraph) => {
    const isAnyValueNonZero = data?.data?.some((item) => {
      const keys = Object.keys(item).filter((key) => key !== 'axisX')
      return keys.some((key) => item[key] !== 0)
    })

    if (data.series && isAnyValueNonZero) {
      setDataShow(data)
    } else {
      setDataShow({} as IStatusGraph)
    }
  }

  const handleDataDownload = (data: IStatusGraph) => {
    if (!data.series || !data.data) {
      setDataCSV([])
      return
    }

    const tmp: IStatusCSV[] = data.series
      .filter(
        (serie: ISerieStatusGraph) => serie.name !== 'Tempo médio de resposta',
      )
      .flatMap((serie: ISerieStatusGraph) =>
        data.data.map((item: IDataStatusGraph) => ({
          status: serie.name,
          requests: item[serie.key],
          date: formatDate(item.axisX),
        })),
      )

    setDataCSV(tmp)
  }

  const filterDataByStatus = useCallback(
    (graph: IStatusGraph) => {
      const status = filterBy?.map((filter) => `axisY${Number(filter) + 1}`)
      const { data, series } = { ...graph }

      const _data = data?.map((data) => {
        const axisY = Object.entries(data).filter(([key]) =>
          key !== 'axisX' ? status?.includes(key) : true,
        )
        return Object.fromEntries(axisY)
      })

      const _series = series?.filter(({ key }) => status?.includes(key))

      return filterBy?.length > 0
        ? Object.assign(
            { ...graph },
            {
              data: _data,
              series: _series,
            },
          )
        : { ...graph }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [filterBy],
  )

  useEffect(() => {
    handleDataShow(filterDataByStatus(dataGraph))
    handleDataDownload(filterDataByStatus(dataGraph))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataGraph, filterBy])

  return (
    <CardBox>
      <CardBoxTitle
        title="Quantidade de requisições por status (Y) na linha do tempo (X)"
        action={
          <>
            {!isLoading && !isError && dataShow.data && dataCSV.length > 0 ? (
              <TooltipInfo
                title="Fazer download do arquivo CSV"
                arrow
                placement="left-start"
                enterTouchDelay={0}
              >
                <IconButton aria-label="download">
                  <IconCsvDownloader
                    filename="status-requisicoes-tempo"
                    headers={headerCSV}
                    data={dataCSV}
                  />
                </IconButton>
              </TooltipInfo>
            ) : (
              ''
            )}
          </>
        }
      />

      <CardContent>
        {isLoading ? (
          <ViewDataLoading />
        ) : isError ? (
          <ViewDataError />
        ) : !dataShow ||
          !dataShow.series ||
          !dataShow.data ||
          dataShow.data.length === 0 ? (
          <ViewDataEmpty />
        ) : (
          <Box
            component="section"
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '100%',
              height: { xs: 380, md: 280 },
            }}
          >
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart
                data={dataShow.data}
                margin={{
                  left: 12,
                  right: 0,
                  top: 6,
                }}
              >
                <Tooltip content={<CustomTooltip />} cursor={false} />
                <CartesianGrid vertical={false} stroke="#f5f5f5" />
                <XAxis
                  tickMargin={12}
                  dataKey="axisX"
                  type="category"
                  tickLine={false}
                  interval="preserveStartEnd"
                  padding={{ left: 40, right: 40 }}
                />
                <YAxis
                  type="number"
                  tickLine={false}
                  tickMargin={12}
                  scale="sqrt"
                  tickFormatter={(value) =>
                    `${formatNumberToScale(value, 1, 1000)}`
                  }
                />
                {dataShow.series.map(
                  (option) =>
                    option.type === 'line' && (
                      <Line
                        key={option.key}
                        type="monotone"
                        dataKey={option.key}
                        name={option.name}
                        stroke={option.color}
                        legendType="plainline"
                        dot={false}
                        isAnimationActive={false}
                        strokeOpacity={
                          activeLegend !== ''
                            ? activeLegend === option.key
                              ? 1
                              : 0.3
                            : 1
                        }
                        strokeWidth={
                          activeLegend !== ''
                            ? activeLegend === option.key
                              ? 3
                              : 1
                            : 2
                        }
                      />
                    ),
                )}
                <Legend
                  iconSize={18}
                  verticalAlign="bottom"
                  wrapperStyle={{ marginBottom: -12 }}
                  onMouseEnter={handleActiveLegend}
                  onMouseLeave={handleDisableLegend}
                />
              </ComposedChart>
            </ResponsiveContainer>
          </Box>
        )}
      </CardContent>
    </CardBox>
  )
}
